import { useState, useEffect, useRef, useCallback } from "react";
import { SMELT_RECIPES } from "../data/recipes";
import { ECHO_SOURCES } from "../data/echoCards";

export function useSmeltQueue({ inventory, materials, setInventory, setMaterials, statsRef, perksRef, queueMaxRef, onComplete, setFuel, fuelMax, instantSmeltRef, onEchoGained }) {
  const onEchoGainedRef = useRef(onEchoGained);
  useEffect(() => { onEchoGainedRef.current = onEchoGained; }, [onEchoGained]);
  const [smeltQueue, setSmeltQueue] = useState([]);
  // smeltProgress: 0-100 progress within the current single run
  // smeltRunsDone: how many runs of the current batch slot are done
  const [smeltProgress, setSmeltProgress] = useState(0);
  const [smeltRunsDone, setSmeltRunsDone] = useState(0);
  const smeltQueueRef = useRef([]);
  const uidCounter = useRef(0);

  useEffect(() => { smeltQueueRef.current = smeltQueue; }, [smeltQueue]);

  // Process the front of the queue — one "run" at a time within the batch
  useEffect(() => {
    if (smeltQueue.length === 0) return;
    const current = smeltQueue[0];
    const recipe = SMELT_RECIPES.find(r => r.id === current.recipeId);
    if (!recipe) { setSmeltQueue(q => q.slice(1)); return; }

    let sm = statsRef.current.smeltSpeed;
    for (const pk of perksRef.current) {
      if (pk.id === "smelt_boost") sm *= 1.4;
      if (pk.id === "rad_storm") sm *= 0.65;
    }
    const tt = recipe.time / sm;
    const tickIncrease = instantSmeltRef?.current ? 101 : 100 / (tt * 20);

    let progress = 0;
    const iv = setInterval(() => {
      progress += tickIncrease;
      if (progress >= 100) {
        clearInterval(iv);
        // Credit output for this single run, scaled by the smeltYield stat.
        // Math.round ensures we always give whole numbers.
        const yieldMult = Math.max(1, statsRef.current.smeltYield ?? 1);
        const actualOutput = Math.round(recipe.outputQty * yieldMult);
        // Fuel-type recipes credit the fuel tank instead of the materials inventory
        if ((recipe.outputType === "fuel" || recipe.output === "fuel") && setFuel) {
          setFuel(f => Math.min(fuelMax ?? 30, f + actualOutput));
        } else {
          setMaterials(m => ({ ...m, [recipe.output]: (m[recipe.output] || 0) + actualOutput }));
        }
        // Award Echo Currency for each completed smelt run
        if (onEchoGainedRef.current) {
          const src = ECHO_SOURCES.smelt;
          const amount = Math.round(src.min + Math.random() * (src.max - src.min));
          onEchoGainedRef.current(amount);
        }

        // Check if more runs remain in this batch
        setSmeltQueue(q => {
          if (q.length === 0) return q;
          const front = q[0];
          const runsDone = (front.runsDone ?? 0) + 1;
          const totalRuns = front.count ?? 1;
          if (runsDone >= totalRuns) {
            // Batch complete — remove the slot
            setSmeltProgress(0);
            setSmeltRunsDone(0);
            if (onComplete) onComplete();
            return q.slice(1);
          }
          // More runs left — update runsDone in-place, keeping same uid so the
          // effect re-fires via a separate mechanism (runsDone state below)
          setSmeltProgress(0);
          setSmeltRunsDone(runsDone);
          return q.map((item, i) => i === 0 ? { ...item, runsDone } : item);
        });
      } else {
        setSmeltProgress(progress);
      }
    }, 50);

    return () => clearInterval(iv);
  // Re-run when: front item changes (new uid) OR a run completes within same batch (runsDone)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smeltQueue[0]?.uid, smeltRunsDone]);

  const queueSmelt = useCallback((rid, count = 1) => {
    const recipe = SMELT_RECIPES.find(r => r.id === rid);
    if (!recipe) return;
    if (smeltQueueRef.current.length >= (queueMaxRef?.current ?? statsRef.current.smeltQueueMax ?? 1)) return;
    // smeltCostMult reduces input requirements (clamped so cost never drops below 10% of base).
    const costMult = Math.max(0.1, statsRef.current.smeltCostMult ?? 1);

    // Deduct cost for ALL runs upfront. Inputs may come from inventory (raw mats)
    // or materials (smelted mats) — check both sources.
    const inv = { ...inventory };
    const mats = { ...materials };
    for (const i of recipe.inputs) {
      const need = Math.ceil(i.qty * costMult) * count;
      const fromInv  = inv[i.id]  || 0;
      const fromMats = mats[i.id] || 0;
      if (fromInv + fromMats < need) return;
    }
    for (const i of recipe.inputs) {
      const need = Math.ceil(i.qty * costMult) * count;
      const fromInv = Math.min(inv[i.id] || 0, need);
      inv[i.id]  = (inv[i.id]  || 0) - fromInv;
      mats[i.id] = (mats[i.id] || 0) - (need - fromInv);
    }
    setInventory(inv);
    setMaterials(mats);
    uidCounter.current++;
    // Store costMult on the slot so removeFromQueue can refund the exact amount deducted
    setSmeltQueue(q => [...q, { uid: uidCounter.current, recipeId: rid, count, runsDone: 0, costMult }]);
  }, [inventory, materials, setInventory, setMaterials, statsRef, queueMaxRef, smeltQueueRef]);

  const removeFromQueue = useCallback((idx) => {
    if (idx <= 0) return;
    setSmeltQueue(q => {
      const item = q[idx];
      if (!item) return q;
      const recipe = SMELT_RECIPES.find(r => r.id === item.recipeId);
      if (recipe) {
        const remaining = (item.count ?? 1) - (item.runsDone ?? 0);
        const cm = item.costMult ?? 1;
        // Refund raw inputs to inventory, smelted inputs to materials
        const invRefund = {}, matsRefund = {};
        for (const inp of recipe.inputs) {
          const qty = Math.ceil(inp.qty * cm) * remaining;
          // Raw debris items go back to inventory, everything else to materials
          if (inventory[inp.id] !== undefined || (!materials || materials[inp.id] === undefined)) {
            invRefund[inp.id] = (invRefund[inp.id] || 0) + qty;
          } else {
            matsRefund[inp.id] = (matsRefund[inp.id] || 0) + qty;
          }
        }
        if (Object.keys(invRefund).length)  setInventory(inv  => { const n = { ...inv  }; for (const [k,v] of Object.entries(invRefund))  n[k] = (n[k] || 0) + v; return n; });
        if (Object.keys(matsRefund).length) setMaterials(mats => { const n = { ...mats }; for (const [k,v] of Object.entries(matsRefund)) n[k] = (n[k] || 0) + v; return n; });
      }
      return [...q.slice(0, idx), ...q.slice(idx + 1)];
    });
  // inventory/materials are read inside setSmeltQueue's updater callback — no stale closure
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setInventory]);

  return { smeltQueue, smeltProgress, smeltRunsDone, smeltQueueRef, queueSmelt, removeFromQueue };
}
